/*
 * Selling increment (master pack) enforcement - Chivari / Multi theme
 *
 * Rule: a customer may buy exactly 1 unit (sample). Anything above 1 must be a
 * multiple of the product's master pack (metafield product.master_pack).
 *   pack 10 -> allowed: 1, 10, 20, 30 ...   2-9 -> 10,  11-19 -> 20 ...
 * Products with no master pack (blank / 0 / 1) are not affected.
 *
 * Covers: product page + quick view qty input / +/- / Add to Cart (incl. sticky bar),
 *         cart page + cart drawer (+, -, typed qty), and a cart-side backstop for
 *         any other add path (recommendations, bundles, etc.).
 * Does NOT touch: checkout, draft orders, orders pushed in via API.
 *
 * Needs: sections/selling-increment-data.liquid (line key -> pack lookup for the cart)
 *        data-master-pack on the PDP qty input (snippets/product-qty-selector.liquid)
 */
(() => {
    if (window.__sellingIncrementLoaded) return;
    window.__sellingIncrementLoaded = true;

    const DATA_SECTION = 'selling-increment-data';

    /* ---------- pure math ---------- */
    const toInt = (v) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : NaN;
    };
    // 1 stays 1; anything above 1 rounds UP to the next multiple of p
    const snapUp = (q, p) => (p <= 1 || q <= 1 ? q : Math.ceil(q / p) * p);
    // next allowed quantity above q
    const stepUp = (q, p) => {
        if (p <= 1) return q + 1;
        if (q < 1) return 1;
        if (q === 1) return p;
        return q - (q % p) + p;
    };
    // next allowed quantity below q (0 = remove)
    const stepDown = (q, p) => {
        if (p <= 1) return q - 1;
        if (q <= 1) return 0;
        if (q <= p) return 1;
        const r = q % p;
        return r ? q - r : q - p;
    };
    // keep within available stock (a < 0 means no limit)
    const clampToAvail = (q, p, a) => {
        if (!(a >= 0) || q <= a) return q;
        if (a >= p) return a - (a % p);
        return a >= 1 ? 1 : q;
    };

    window.SellingIncrement = Object.freeze({ snapUp, stepUp, stepDown, clampToAvail });

    /* ---------- tiny toast ---------- */
    let toastEl = null;
    let toastTimer = null;
    function toast(msg) {
        try {
            if (!toastEl) {
                toastEl = document.createElement('div');
                toastEl.setAttribute('role', 'status');
                toastEl.setAttribute('aria-live', 'polite');
                Object.assign(toastEl.style, {
                    position: 'fixed',
                    left: '50%',
                    bottom: '24px',
                    transform: 'translateX(-50%)',
                    background: '#1a1a1a',
                    color: '#fff',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    zIndex: '2147483000',
                    maxWidth: '90vw',
                    textAlign: 'center',
                    boxShadow: '0 4px 16px rgba(0,0,0,.25)',
                    transition: 'opacity .25s',
                    opacity: '0',
                    pointerEvents: 'none'
                });
                document.body.appendChild(toastEl);
            }
            toastEl.textContent = msg;
            toastEl.style.opacity = '1';
            clearTimeout(toastTimer);
            toastTimer = setTimeout(() => { toastEl.style.opacity = '0'; }, 5000);
        } catch (err) { /* cosmetic only */ }
    }
    const adjustedMsg = (pack, qty) => `Sold in multiples of ${pack}. Quantity adjusted to ${qty}.`;

    /* ---------- urls ---------- */
    const urls = () => window.dynamicURLs || {};
    const cartJsonUrl = () => `${urls().cart || '/cart'}.js`;
    const cartChangeUrl = () => `${urls().cartChange || '/cart/change'}.js`;
    const dataUrl = () => `${urls().root || '/'}?section_id=${DATA_SECTION}&t=${Date.now()}`;

    /* ---------- cart lookup (line key -> { q, p, a }) ---------- */
    let lines = {};
    let refreshing = null;
    const attempts = {};

    async function loadLines() {
        const res = await fetch(dataUrl(), { credentials: 'same-origin' });
        if (!res.ok) throw new Error('selling-increment data ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const node = doc.getElementById('selling-increment-data');
        const data = node ? JSON.parse(node.textContent) : null;
        lines = (data && data.lines) || {};
    }

    // Backstop: fix any cart line that is above 1 and not a multiple of its pack.
    async function reconcile() {
        const bad = [];
        Object.keys(lines).forEach((key) => {
            const l = lines[key];
            const invalid = l.p > 1 && l.q > 1 && l.q % l.p !== 0;
            if (!invalid) { delete attempts[key]; return; }
            if ((attempts[key] || 0) < 2) bad.push(key);
        });
        if (!bad.length) return;

        let changed = false;
        for (const key of bad) {
            const l = lines[key];
            attempts[key] = (attempts[key] || 0) + 1;
            const target = clampToAvail(snapUp(l.q, l.p), l.p, l.a);
            if (target === l.q) continue;
            try {
                const res = await fetch(cartChangeUrl(), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ id: key, quantity: target })
                });
                if (res.ok) {
                    changed = true;
                    toast(adjustedMsg(l.p, target));
                }
            } catch (err) { console.error(err); }
        }
        if (changed) document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    // Show "sold in multiples" note under each cart line (cart page + drawer) that has a pack above 1
    function annotate() {
        try {
            document.querySelectorAll('[data-item-key]').forEach((el) => {
                const l = lines[el.getAttribute('data-item-key')];
                if (!l) return; // not a cart line we know about
                const next = el.nextElementSibling;
                const existing = next && next.classList.contains('si-line-note') ? next : null;
                if (!(l.p > 1)) {
                    if (existing) existing.remove();
                    return;
                }
                const text = `Sold in multiples of ${l.p}. A single unit can be ordered as a sample.`;
                if (existing) {
                    if (existing.textContent !== text) existing.textContent = text;
                    return;
                }
                const note = document.createElement('p');
                note.className = 'si-line-note';
                note.textContent = text;
                const inDrawer = !!el.closest('cart-drawer');
                Object.assign(note.style, {
                    // main cart: indent to line up with the card contents and leave room below; drawer unchanged
                    margin: inDrawer ? '0.25rem 0 0.75rem' : '0',
                    padding: inDrawer ? '0' : '0.25rem 1.5rem 1.25rem',
                    fontSize: '0.85em',
                    lineHeight: '1.4',
                    opacity: '0.8'
                });
                el.insertAdjacentElement('afterend', note);
            });
        } catch (err) { console.error(err); }
    }

    function refreshLines() {
        if (refreshing) return refreshing;
        refreshing = (async () => {
            try {
                await loadLines();
                annotate();
                await reconcile();
            } catch (err) {
                console.error(err);
            } finally {
                refreshing = null;
            }
        })();
        return refreshing;
    }

    /* ---------- product page: +/- buttons ---------- */
    function handlePdpButton(e, btn) {
        const wrap = btn.closest('product-qty-input');
        const input = wrap && wrap.querySelector('input[data-master-pack]');
        if (!input) return;
        const pack = toInt(input.dataset.masterPack);
        if (!(pack > 1)) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const isPlus = btn.getAttribute('data-element') === 'button-plus';
        let q = toInt(input.value);
        if (!(q >= 1)) q = 1;
        q = snapUp(q, pack);

        let next = isPlus ? stepUp(q, pack) : Math.max(1, stepDown(q, pack));
        const max = toInt(input.getAttribute('max'));
        if (Number.isFinite(max)) next = clampToAvail(next, pack, max);

        input.value = next;
        // lets the theme's own component sync its internal value and button states
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------- cart page / drawer: +/- buttons ---------- */
    function handleCartButton(e, cq) {
        const l = lines[cq.getAttribute('key')];
        if (!l || !(l.p > 1)) return;

        const target = toInt(cq.getAttribute('value'));
        if (!(target > 0)) return; // remove button or unknown: leave alone

        // button value is always (current qty +/- 1); if that doesn't match our lookup it is stale
        if (Math.abs(target - l.q) !== 1) return;

        const up = target > l.q;
        let next = up ? stepUp(l.q, l.p) : stepDown(l.q, l.p);
        if (next < 1) return; // minus from 1 = remove, default behavior
        next = clampToAvail(next, l.p, l.a);

        if (next === l.q) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }
        if (typeof cq.setProp === 'function') {
            cq.setProp('value', next);
        } else {
            cq.setAttribute('value', next);
        }
    }

    function onClick(e) {
        const t = e.target;
        if (!(t instanceof Element)) return;

        const btn = t.closest('product-qty-input [data-element="button-plus"], product-qty-input [data-element="button-minus"]');
        if (btn) { handlePdpButton(e, btn); return; }

        const cq = t.closest('cart-qty');
        if (cq && !cq.hasAttribute('input')) handleCartButton(e, cq);
    }

    /* ---------- typed quantities (product page + cart page) ---------- */
    function onChange(e) {
        const t = e.target;
        if (!(t instanceof HTMLInputElement)) return;

        // product page / quick view
        if (t.hasAttribute('data-master-pack')) {
            const pack = toInt(t.dataset.masterPack);
            if (!(pack > 1)) return;
            let v = t.valueAsNumber;
            if (!Number.isFinite(v) || v < 1) return; // theme resets these to 1
            v = Math.floor(v);
            let s = snapUp(v, pack);
            const max = toInt(t.getAttribute('max'));
            if (Number.isFinite(max)) s = clampToAvail(s, pack, max);
            if (s !== t.valueAsNumber) {
                t.value = s;
                if (s !== v) toast(adjustedMsg(pack, s));
            }
            return;
        }

        // cart page
        const cq = t.closest('cart-qty');
        if (cq && cq.hasAttribute('input') && t.name === 'updates[]') {
            const l = lines[cq.getAttribute('key')];
            if (!l || !(l.p > 1)) return;
            const v = toInt(t.value);
            if (!Number.isFinite(v) || v <= 1) return;
            const s = clampToAvail(snapUp(v, l.p), l.p, l.a);
            if (s !== v) {
                t.value = s;
                toast(adjustedMsg(l.p, s));
            }
        }
    }

    /* ---------- add to cart: account for what is already in the cart ---------- */
    async function onSubmit(e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('[data-type="add-to-cart-form"]')) return;
        if (form.__siBypass) { form.__siBypass = false; return; }

        const qtyInput = Array.from(form.elements).find((el) => el.name === 'quantity' && el.hasAttribute('data-master-pack'));
        if (!qtyInput) return;
        const pack = toInt(qtyInput.dataset.masterPack);
        if (!(pack > 1)) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        let add = toInt(qtyInput.value);
        if (!(add >= 1)) add = 1;

        let existing = 0;
        try {
            const variantId = Number(new FormData(form).get('id'));
            const res = await fetch(cartJsonUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
            const cart = await res.json();
            existing = cart.items
                .filter((i) => i.variant_id === variantId)
                .reduce((sum, i) => sum + i.quantity, 0);
        } catch (err) {
            console.error(err);
        }

        // A lone sample unit in the cart is replaced, not added to: 1 in cart + order of 8 => cart ends at 8
        const replaceSample = existing === 1 && add > 1;
        const targetTotal = replaceSample ? snapUp(add, pack) : snapUp(existing + add, pack);
        const finalAdd = Math.max(1, targetTotal - existing);
        if (finalAdd !== add) qtyInput.value = finalAdd;
        if (replaceSample) {
            if (targetTotal !== add) {
                toast(`Sold in multiples of ${pack}. Your sample unit was replaced and your cart now has ${targetTotal}.`);
            }
        } else if (finalAdd !== add) {
            toast(`Sold in multiples of ${pack}. Added ${finalAdd} so your cart total is ${existing + finalAdd}.`);
        }

        form.__siBypass = true;
        try {
            form.requestSubmit();
        } finally {
            qtyInput.value = add; // put the shopper's chosen quantity back
        }
    }

    /* ---------- wiring ---------- */
    document.addEventListener('click', onClick, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('submit', onSubmit, true);

    const hasCartNodes = (n) =>
        n.nodeType === 1 && (n.matches('cart-qty, [data-item-key]') || n.querySelector('cart-qty, [data-item-key]'));

    let timer = null;
    const scheduleRefresh = () => {
        clearTimeout(timer);
        timer = setTimeout(refreshLines, 250);
    };

    new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const n of m.addedNodes) {
                if (hasCartNodes(n)) { scheduleRefresh(); return; }
            }
        }
    }).observe(document.documentElement, { childList: true, subtree: true });

    if (document.querySelector('cart-qty')) refreshLines();
})();